import type {
  CampaignCreationCapabilities,
  SmartCreationStrategyOperation,
  SmartCreationTargetingStrategyBranch,
} from "@adpilot/shared";
import { IconArrowLeft, IconX } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSpCategorySearch } from "@/hooks/use-sp-category-search";
import { fetchSpCategoryBrands } from "@/lib/campaign-creation-api";
import { fetchSmartCreationSelectedBrands } from "@/lib/smart-creation-api";

interface CategoryOption {
  id: string;
  name: string;
  path: string;
}

interface BrandOption {
  id: string;
  name: string;
}

interface Props {
  planId: string;
  shopId: string;
  planRevision: number;
  branch: SmartCreationTargetingStrategyBranch;
  allBranches: SmartCreationTargetingStrategyBranch[];
  capabilities: CampaignCreationCapabilities;
  mutationPending?: boolean;
  onApply: (operations: SmartCreationStrategyOperation[], revision: number) => Promise<boolean>;
  onCancel: () => void;
  onComplete: () => void;
  onDirtyChange: (dirty: boolean) => void;
}

export function StrategyCategoryBrandEditor({
  planId,
  shopId,
  planRevision,
  branch,
  allBranches,
  capabilities,
  mutationPending = false,
  onApply,
  onCancel,
  onComplete,
  onDirtyChange,
}: Props) {
  const { t } = useTranslation("smartCreation");
  const [category, setCategory] = useState<CategoryOption | null>(null);
  const [brands, setBrands] = useState<BrandOption[]>([]);
  const [brandQuery, setBrandQuery] = useState("");
  const [brand, setBrand] = useState<BrandOption | null>(null);
  const [bid, setBid] = useState(String(capabilities.bid.min));
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const categorySearch = useSpCategorySearch(shopId, {
    onError: () => setError(t("strategy.brandEditor.categoriesFailed")),
  });
  const [ownBrands, setOwnBrands] = useState<
    | { status: "loading" }
    | { status: "error" }
    | { status: "ready"; brands: string[]; missingSkus: string[] }
  >({ status: "loading" });

  useEffect(() => {
    if (branch.kind !== "branded") return;
    let stale = false;
    setOwnBrands({ status: "loading" });
    void fetchSmartCreationSelectedBrands(planId, shopId)
      .then((result) => {
        if (!stale) setOwnBrands({ status: "ready", ...result });
      })
      .catch(() => {
        if (!stale) setOwnBrands({ status: "error" });
      });
    return () => {
      stale = true;
    };
  }, [branch.kind, planId, shopId]);

  useEffect(() => {
    if (!category) {
      setBrands([]);
      return;
    }
    let stale = false;
    setLoadingBrands(true);
    void fetchSpCategoryBrands(shopId, category.id)
      .then((items) => {
        if (!stale) setBrands(items);
      })
      .catch(() => {
        if (!stale) setError(t("strategy.brandEditor.brandsFailed"));
      })
      .finally(() => {
        if (!stale) setLoadingBrands(false);
      });
    return () => {
      stale = true;
    };
  }, [category, shopId, t]);

  const filteredBrands = useMemo(() => {
    const needle = brandQuery.trim().toLocaleLowerCase();
    const selectedNames =
      branch.kind === "branded" &&
      ownBrands.status === "ready" &&
      ownBrands.missingSkus.length === 0
        ? new Set(ownBrands.brands.map((name) => name.trim().toLocaleLowerCase()))
        : null;
    return brands
      .filter(
        (item) =>
          item.name.toLocaleLowerCase().includes(needle) &&
          (branch.kind !== "branded" || selectedNames?.has(item.name.trim().toLocaleLowerCase())),
      )
      .slice(0, 50);
  }, [brandQuery, branch.kind, brands, ownBrands]);
  const ownBrandEligible =
    branch.kind !== "branded" ||
    (ownBrands.status === "ready" &&
      ownBrands.missingSkus.length === 0 &&
      brand !== null &&
      ownBrands.brands.some(
        (name) => name.trim().toLocaleLowerCase() === brand.name.trim().toLocaleLowerCase(),
      ));
  const parsedBid = Number(bid);
  const bidValid =
    Number.isFinite(parsedBid) &&
    parsedBid >= capabilities.bid.min &&
    parsedBid <= capabilities.bid.max &&
    Number(parsedBid.toFixed(capabilities.bid.decimals)) === parsedBid;
  const duplicate =
    category !== null &&
    brand !== null &&
    allBranches.some((candidate) =>
      candidate.targets.some(
        (target) =>
          target.intent.type === "category_brand" &&
          target.intent.categoryId === category.id &&
          target.intent.brandId === brand.id,
      ),
    );

  async function apply(): Promise<void> {
    if (
      !category ||
      !brand ||
      !bidValid ||
      !ownBrandEligible ||
      duplicate ||
      submitting ||
      mutationPending ||
      (branch.kind !== "competitor" && branch.kind !== "branded")
    )
      return;
    setSubmitting(true);
    setError(null);
    try {
      const operations: SmartCreationStrategyOperation[] = [
        {
          operation: "add_target",
          strategyKind: branch.kind,
          target: {
            intent: { type: "category_brand", categoryId: category.id, brandId: brand.id },
            bid: parsedBid,
          },
        },
        {
          operation: "set_lane_enabled",
          strategyKind: branch.kind,
          lane: branch.kind === "competitor" ? "competitor_brand" : "branded_own_brand",
          enabled: true,
        },
      ];
      if (await onApply(operations, planRevision)) {
        onDirtyChange(false);
        onComplete();
      } else {
        setError(t("strategy.brandEditor.applyFailed"));
      }
    } catch {
      setError(t("strategy.brandEditor.applyFailed"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="space-y-4" data-category-brand-editor>
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t("strategy.brandEditor.back")}
          onClick={onCancel}
        >
          <IconArrowLeft className="size-4" />
        </Button>
        <div>
          <h2
            id="smart-creation-brand-editor-title"
            tabIndex={-1}
            className="text-base font-semibold"
          >
            {t("strategy.brandEditor.title")}
          </h2>
          <p className="text-xs text-muted-foreground">
            {t(
              branch.kind === "branded"
                ? "strategy.brandEditor.ownBrandDescription"
                : "strategy.brandEditor.description",
            )}
          </p>
          {branch.kind === "branded" && ownBrands.status === "ready" && ownBrands.brands.length ? (
            <p className="mt-1 text-xs text-muted-foreground">
              {t("strategy.brandEditor.selectedProductBrands", {
                brands: `${ownBrands.brands.slice(0, 3).join(", ")}${
                  ownBrands.brands.length > 3 ? ` +${ownBrands.brands.length - 3}` : ""
                }`,
              })}
            </p>
          ) : null}
        </div>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-4 rounded-md border border-line bg-surface p-4">
          <div>
            <label htmlFor="brand-category-query" className="mb-1.5 block text-sm font-medium">
              {t("strategy.brandEditor.category")}
            </label>
            <Input
              id="brand-category-query"
              data-brand-category-query
              size="sm"
              value={categorySearch.query}
              onChange={(event) => {
                categorySearch.setQuery(event.target.value);
                setCategory(null);
                setBrand(null);
                setError(null);
                onDirtyChange(false);
              }}
              placeholder={t("strategy.brandEditor.categoryPlaceholder")}
            />
            {categorySearch.query.trim().length < 2 ? (
              <p className="mt-1.5 text-xs text-muted-foreground">
                {t("strategy.brandEditor.searchHint")}
              </p>
            ) : null}
            {categorySearch.loading ? (
              <p className="mt-2 text-xs text-muted-foreground" role="status">
                {t("strategy.brandEditor.searching")}
              </p>
            ) : null}
            {!category && categorySearch.categories.length > 0 ? (
              <div
                className="mt-2 max-h-52 overflow-y-auto rounded-md border border-line"
                role="listbox"
                aria-label={t("strategy.brandEditor.categoryResults")}
              >
                {categorySearch.categories.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    role="option"
                    aria-selected={false}
                    data-category-option={option.id}
                    className="block w-full border-b border-line px-3 py-2 text-left text-xs last:border-0 hover:bg-subtle focus-visible:bg-subtle"
                    onClick={() => {
                      setCategory(option);
                      setBrand(null);
                      setBrandQuery("");
                      setError(null);
                      onDirtyChange(false);
                    }}
                  >
                    <span className="block font-medium">{option.name}</span>
                    <span className="text-muted-foreground">
                      {option.path} · {option.id}
                    </span>
                  </button>
                ))}
                {categorySearch.hasMore ? (
                  <Button
                    data-category-load-more
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    disabled={categorySearch.loading}
                    onClick={categorySearch.loadMore}
                  >
                    {t("strategy.brandEditor.loadMore")}
                  </Button>
                ) : null}
              </div>
            ) : null}
            {category ? (
              <div className="mt-2 flex items-center justify-between rounded-md border border-line bg-subtle px-3 py-2 text-xs">
                <span>
                  {category.path} · {category.id}
                </span>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label={t("strategy.brandEditor.clearCategory")}
                  onClick={() => {
                    setCategory(null);
                    setBrand(null);
                    onDirtyChange(false);
                  }}
                >
                  <IconX className="size-3" />
                </Button>
              </div>
            ) : null}
          </div>
          {category ? (
            <div>
              <label htmlFor="brand-query" className="mb-1.5 block text-sm font-medium">
                {t("strategy.brandEditor.brand")}
              </label>
              <Input
                id="brand-query"
                size="sm"
                value={brandQuery}
                onChange={(event) => setBrandQuery(event.target.value)}
                placeholder={t("strategy.brandEditor.brandPlaceholder")}
              />
              {loadingBrands ? (
                <p className="mt-2 text-xs text-muted-foreground" role="status">
                  {t("strategy.brandEditor.loadingBrands")}
                </p>
              ) : null}
              {branch.kind === "branded" && ownBrands.status === "loading" ? (
                <p className="mt-2 text-xs text-muted-foreground" role="status">
                  {t("strategy.brandEditor.loadingOwnBrands")}
                </p>
              ) : null}
              {branch.kind === "branded" && ownBrands.status === "error" ? (
                <p className="mt-2 text-xs text-destructive" role="alert">
                  {t("strategy.brandEditor.ownBrandsFailed")}
                </p>
              ) : null}
              {branch.kind === "branded" &&
              ownBrands.status === "ready" &&
              ownBrands.missingSkus.length > 0 ? (
                <p className="mt-2 text-xs text-destructive" role="alert">
                  {t("strategy.brandEditor.missingProductBrands", {
                    count: ownBrands.missingSkus.length,
                  })}
                </p>
              ) : null}
              {!loadingBrands &&
              (branch.kind !== "branded" || ownBrands.status === "ready") &&
              filteredBrands.length === 0 &&
              (branch.kind !== "branded" ||
                (ownBrands.status === "ready" && ownBrands.missingSkus.length === 0)) ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  {t(
                    branch.kind === "branded"
                      ? "strategy.brandEditor.noMatchingOwnBrands"
                      : "strategy.brandEditor.noBrands",
                  )}
                </p>
              ) : null}
              <div
                className="mt-2 max-h-52 overflow-y-auto rounded-md border border-line"
                role="listbox"
                aria-label={t("strategy.brandEditor.brandResults")}
              >
                {filteredBrands.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    role="option"
                    aria-selected={brand?.id === option.id}
                    data-brand-option={option.id}
                    className="flex w-full items-center justify-between border-b border-line px-3 py-2 text-left text-xs last:border-0 hover:bg-subtle focus-visible:bg-subtle aria-selected:bg-accent"
                    onClick={() => {
                      setBrand(option);
                      setError(null);
                      onDirtyChange(true);
                    }}
                  >
                    <span className="font-medium">{option.name}</span>
                    <span className="text-muted-foreground">{option.id}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
        <div className="space-y-4 rounded-md border border-line bg-surface p-4">
          <h3 className="text-sm font-semibold">{t("strategy.brandEditor.selectedTarget")}</h3>
          {category && brand ? (
            <div className="space-y-3 rounded-md border border-line bg-subtle p-3 text-sm">
              <div className="font-medium">{brand.name}</div>
              <div className="text-xs text-muted-foreground">
                {category.path} · {category.id} / {brand.id}
              </div>
              <div>
                <label htmlFor="brand-target-bid" className="mb-1 block text-xs font-medium">
                  {t("strategy.brandEditor.bid")}
                </label>
                <Input
                  id="brand-target-bid"
                  size="sm"
                  type="number"
                  min={capabilities.bid.min}
                  max={capabilities.bid.max}
                  step={10 ** -capabilities.bid.decimals}
                  value={bid}
                  aria-invalid={!bidValid}
                  onChange={(event) => {
                    setBid(event.target.value);
                    onDirtyChange(true);
                  }}
                />
                {!bidValid ? (
                  <p className="mt-1 text-xs text-destructive">
                    {t("strategy.brandEditor.bidRange", {
                      min: capabilities.bid.min,
                      max: capabilities.bid.max,
                    })}
                  </p>
                ) : null}
                {duplicate ? (
                  <p className="mt-1 text-xs text-destructive">
                    {t("strategy.brandEditor.duplicate")}
                  </p>
                ) : null}
              </div>
            </div>
          ) : (
            <p className="rounded-md border border-dashed border-line p-6 text-center text-xs text-muted-foreground">
              {t("strategy.brandEditor.emptySelection")}
            </p>
          )}
          <p className="text-xs text-muted-foreground">{t("strategy.brandEditor.shopScope")}</p>
          {error ? (
            <p className="text-xs text-destructive" role="alert">
              {error}
            </p>
          ) : null}
          <Button
            data-brand-target-apply
            disabled={
              !category || !brand || !bidValid || !ownBrandEligible || duplicate || mutationPending
            }
            loading={submitting}
            onClick={() => void apply()}
          >
            {t("strategy.brandEditor.apply")}
          </Button>
        </div>
      </div>
    </section>
  );
}
